import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Dropdown,
  Tooltip,
  OverlayTrigger,
  Form,
} from "react-bootstrap";
import Select, { components } from "react-select";
import { useSearchParams, useNavigate } from "react-router-dom";
import RetreatRoasterApi from "../../../api/retreatRosterApi";
import RetreatApi from "../../../api/retreatApi";
import AuthApi from "../../../api/authApi";
import CustomDataTable from "../../../components/CustomDataTable";
import { toast } from "react-toastify";
import moment from "moment";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const BuildMyTeam = () => {
  const [searchParams] = useSearchParams();
  const retreatId = searchParams.get("retreatId");
  const navigate = useNavigate();
  const retreatRoastApi = new RetreatRoasterApi();
  const retreatApi = new RetreatApi();
  const authApi = new AuthApi();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [retreatDetail, setRetreatDetail] = useState<any>(null);
  const [parishOptions, setParishOptions] = useState<any[]>([]);

  // Filter States
  const [selectedLanguages, setSelectedLanguages] = useState<any[]>([]); // Array of strings: ['English', 'Spanish']
  const [selectedParishes, setSelectedParishes] = useState<any[]>([]); // Array of {value, label}
  const [filterOption, setFilterOption] = useState("neverTeamed");

  const filterOptions = [
    {
      value: "neverTeamed",
      label: "Couples who have NEVER teamed from my Parish",
    },
    { value: "neverTeamedBefore", label: "Couples who NEVER teamed before" },
    {
      value: "teamedPast5Years",
      label: "Couples who have teamed in the past 5 years",
    },
    { value: "teamedBefore", label: "Couples who have teamed before" },
    { value: "headCouple", label: "Couples who have served as a Head Couple" },
    { value: "givenTalk", label: "Couples who have given a Talk" },
    { value: "allAttendeeTypes", label: "All Attendee Types" },
  ];

  const languageOptions = ["English", "Spanish"];
  const CustomOption = (props: any) => {
    return (
      <div>
        <components.Option {...props}>
          <div className="d-flex align-items-center">
            <input
              type="checkbox"
              checked={props.isSelected}
              onChange={() => null}
              className="form-check-input me-3"
              style={{
                width: "18px",
                height: "18px",
                cursor: "pointer",
              }}
            />
            <span
              style={{
                cursor: "pointer",
                fontSize: "0.95rem",
                fontWeight: props.isSelected ? "600" : "400",
              }}
            >
              {props.label}
            </span>
          </div>
        </components.Option>
      </div>
    );
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "12px",
      padding: "6px 8px",
      border: state.isFocused ? "1px solid #3190fa" : "1px solid #dee2e6",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(49, 144, 250, 0.1)" : "none",
      transition: "all 0.3s ease",
      "&:hover": {
        border: "1px solid #3190fa",
      },
      backgroundColor: "#fff",
      cursor: "pointer",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#adb5bd",
      fontSize: "0.95rem",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#212529",
      fontWeight: "500",
      fontSize: "0.95rem",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#eef2ff",
      borderRadius: "6px",
      padding: "2px 4px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#3190fa",
      fontWeight: "600",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#3190fa",
      "&:hover": {
        backgroundColor: "#3190fa",
        color: "#fff",
      },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#3190fa",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
      border: "1px solid #f1f3f5",
      padding: "8px",
      zIndex: 100,
      marginTop: "8px",
      animation: "fadeIn 0.2s ease-out",
    }),
    option: (base, state) => ({
      ...base,
      borderRadius: "8px",
      backgroundColor: state.isSelected
        ? "#f0f2ff"
        : state.isFocused
          ? "#f8f9fa"
          : "transparent",
      color: state.isSelected ? "#3190fa" : "#495057",
      cursor: "pointer",
      fontWeight: state.isSelected ? "600" : "400",
      padding: "10px 12px",
      "&:active": {
        backgroundColor: "#e8ebff",
      },
    }),
  };

  useEffect(() => {
    fetchParishes();
  }, []);

  useEffect(() => {
    if (retreatId) {
      fetchRetreatDetails();
    }
  }, [retreatId]);

  const fetchParishes = async () => {
    try {
      const res = await authApi.getPerishList();
      if (res.success) {
        const sortedParishes = res.data
          .sort((a, b) => (a.parish || "").localeCompare(b.parish || ""))
          .map((p) => ({
            value: p.id,
            label: p.parish,
          }));
        setParishOptions(sortedParishes);
      }
    } catch (error) {
      console.error("Error fetching parishes", error);
    }
  };

  const fetchRetreatDetails = async () => {
    try {
      const res = await retreatApi.getRetreatDetail({ id: retreatId });
      if (res.success && res.data && res.data.length > 0) {
        const detail = res.data[0];
        setRetreatDetail(detail);
        // Default Behavior: Auto-select current retreat language and parish
        if (detail.language) {
          setSelectedLanguages([capitalize(detail.language)]);
        }
        if (detail.parishId) {
          const pName =
            detail.myparish?.parish || detail.parishName || "My Parish";
          setSelectedParishes([{ value: detail.parishId, label: pName }]);
        }
      }
    } catch (error) {
      console.error("Error fetching retreat details", error);
    }
  };

  useEffect(() => {
    // Only fetch if we have at least one language (parish can be empty for global search)
    if (retreatId && selectedLanguages.length > 0) {
      fetchData();
    }
  }, [selectedLanguages, selectedParishes, filterOption]);

  const fetchData = async (pageNo: any = 1, size: any = 100, searchKeyword: any = null, sortBy: any = {}, 
  ) => {
    setLoading(true);
    try {
      const parishIds = selectedParishes.some((p) => p.value === "all")
        ? ["all"]
        : selectedParishes.map((p) => p.value);

      const res = await retreatRoastApi.buildMyTeam({
        retreatId: retreatId,
        languages: selectedLanguages,
        parishIds: parishIds,
        filterOption: filterOption,
        sortBy: sortBy,
      });

      if (res.success) {
        setData(res.data);
      } else {
        toast.error(res.msg);
      }
    } catch (error) {
      toast.error("Error fetching data");
    }
    setLoading(false);
  };

  const handleLanguageChange = (lang: any) => {
    setSelectedLanguages((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang],
    );
  };

  const handleParishChange = (selected: any) => {
    if (selected && selected.some((p) => p.value === "all")) {
      setSelectedParishes([{ value: "all", label: "Select All Parishes" }]);
    } else if (selected && selected.some((p) => p.value === "none")) {
      setSelectedParishes([]);
    } else {
      setSelectedParishes(selected || []);
    }
  };

  const capitalize = (str: any) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  const renderTooltip = (props: any) => (
    <Tooltip id="button-tooltip">{props}</Tooltip>
  );

  const columns = [
    {
      id: "retreatName",
      name: "Retreat Name",
      selector: (row) => row.retreatName,
      sortable: true,
      width: "280px",
      cell: (row) => {
        const fullName = `${row?.retreatParishName}${row.language ? ` (${capitalize(row.language)})` : ""} ${row.location ? ` ${capitalize(row.location)}` : ""}`;
        const displayDate = row.retreatDate
          ? moment(row.retreatDate).format("MMM DD, YYYY")
          : "";
        return (
          <div className="py-2">
            <OverlayTrigger
              placement="bottom"
              overlay={renderTooltip(fullName)}
            >
              <a
                href={`/retreatRoster?retreatId=${row.retreatId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(
                    `/retreatRoster?retreatId=${row.retreatId}`,
                    "_blank",
                  );
                }}
                className="d-block text-truncate fw-bold"
                style={{ maxWidth: "260px", color: "#3190fa" }}
              >
                {fullName}
              </a>
            </OverlayTrigger>
            {displayDate && (
              <div className="small text-muted">{displayDate}</div>
            )}
          </div>
        );
      },
    },
    {
      id: "retreatId",
      name: "Retreat Id",
      selector: (row) => row.retreatId,
      sortable: true,
      width: "120px",
    },
    {
      id: "attendeeType",
      name: "Attendee Type",
      selector: (row) => row.attendeeType,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <span
          className={`badge ${row.attendeeType === "Team" ? "bg-success" : "bg-info text-dark"} rounded-pill`}
        >
          {row.attendeeType}
        </span>
      ),
    },

    {
      id: "hisLastName",
      name: "His Last Name",
      selector: (row) => row.hisLastName,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.hisLastName)}
        >
          <span className="text-truncate">{row.hisLastName}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "hisFirstName",
      name: "His First Name",
      selector: (row) => row.hisFirstName,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.hisFirstName)}
        >
          <span className="text-truncate">{row.hisFirstName}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "hisEmail",
      name: "His Email",
      selector: (row) => row.hisEmail,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.hisEmail)}
        >
          <span className="text-truncate">{row.hisEmail}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "hisMobile",
      name: "His Mobile",
      selector: (row) => row.hisMobile,
      sortable: true,
      width: "150px",
    },
    {
      id: "herLastName",
      name: "Her Last Name",
      selector: (row) => row.herLastName,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.herLastName)}
        >
          <span className="text-truncate">{row.herLastName}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "herFirstName",
      name: "Her First Name",
      selector: (row) => row.herFirstName,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.herFirstName)}
        >
          <span className="text-truncate">{row.herFirstName}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "herEmail",
      name: "Her Email",
      selector: (row) => row.herEmail,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          overlay={renderTooltip(row.herEmail)}
        >
          <span className="text-truncate">{row.herEmail}</span>
        </OverlayTrigger>
      ),
    },
    {
      id: "herMobile",
      name: "Her Mobile",
      selector: (row) => row.herMobile,
      sortable: true,
      width: "150px",
    },

    // {
    //   id: "retreatParishName",
    //   name: "Retreat Parish",
    //   selector: (row) => row.retreatParishName,
    //   sortable: true,
    //   width: "200px",
    //   cell: (row) => (
    //     <OverlayTrigger
    //       placement="bottom"
    //       overlay={renderTooltip(row.retreatParishName)}
    //     >
    //       <span className="text-truncate">{row.retreatParishName}</span>
    //     </OverlayTrigger>
    //   ),
    // },
    {
      id: "countRetreatant",
      name: "# Retreatant Couples",
      selector: (row) => row && row.countRetreatant,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div className="tooltip-static-demo">
          <div
            className="bs-tooltip-bottom bs-tooltip-bottom-demo tooltip-info"
            data-bs-toggle="tooltip"
            data-bs-placement="bottom"
          >
            <div className="arrow"></div>
            <a
              href={`/retreatRoster?retreatId=${row.retreatId}&flag=1`}
              style={{ textDecoration: "none" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="tooltip-inner">{row && row.countRetreatant}</div>
            </a>
          </div>
        </div>
      ),
    },
    {
      id: "countTeam",
      name: "# Team Couples",
      selector: (row) => row && row.countTeam,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <div className="tooltip-static-demo">
          <div
            className="bs-tooltip-bottom bs-tooltip-bottom-demo tooltip-info"
            data-bs-toggle="tooltip"
            data-bs-placement="bottom"
          >
            <div className="arrow"></div>
            <a
              href={`/retreatRoster?retreatId=${row.retreatId}&flag=2`}
              style={{ textDecoration: "none" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="tooltip-inner">{row && row.countTeam}</div>
            </a>
          </div>
        </div>
      ),
    },
    {
      id: "language",
      name: "Language",
      selector: (row) => row.language,
      sortable: true,
      width: "120px",
      cell: (row) => capitalize(row.language),
    },
    {
      id: "parishName",
      name: "Couple Parish",
      selector: (row) => row.parishName,
      sortable: true,
      width: "200px",
    },
    {
      id: "roleServed",
      name: "Role Served",
      selector: (row) => row.roleServed,
      sortable: true,
      width: "150px",
    },
    {
      id: "serviceTitle",
      name: "Service Title",
      selector: (row) => row.service,
      sortable: true,
      width: "150px",
    },
    {
      id: "talkGiven",
      name: "Talk Given",
      selector: (row) => row.talkGiven,
      sortable: true,
      width: "150px",
    },
  ];

  const formatRetreatName = (item: any) => {
    return `${item?.retreatParishName || ""}${item.language ? ` (${capitalize(item.language)})` : ""} ${item.location ? ` ${capitalize(item.location)}` : ""}`.trim();
  };

  const getExportCellValue = (column: any, item: any) => {
    switch (column.id) {
      case "retreatName": {
        const name = formatRetreatName(item);
        const date = item.retreatDate
          ? moment(item.retreatDate).format("MMM DD, YYYY")
          : "";
        return date ? `${name}\n${date}` : name;
      }
      case "language":
        return capitalize(item.language) || "";
      case "serviceTitle":
        return item.service || "";
      default:
        return column.selector(item) ?? "";
    }
  };

  const exportHeaders = columns.map((column) => column.name);

  const exportToExcel = () => {
    if (data.length === 0) {
      toast.warning("No data to export");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(
      data.map((item) => {
        const row = {};
        columns.forEach((column) => {
          row[column.name] = getExportCellValue(column, item);
        });
        return row;
      }),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TeamMembers");
    XLSX.writeFile(wb, "BuildMyTeam.xlsx");
  };

  const exportToPDF = () => {
    if (data.length === 0) {
      toast.warning("No data to export");
      return;
    }
    const doc = new jsPDF("landscape", "pt", "a4");

    doc.setFontSize(16);
    doc.setTextColor(31, 59, 179);
    doc.text("Build My Team Report", 14, 25);

    const tableData = data.map((item) =>
      columns.map((column) => getExportCellValue(column, item)),
    );

    (doc as any).autoTable({
      head: [exportHeaders],
      body: tableData,
      startY: 35,
      theme: "grid",
      styles: {
        fontSize: 5.5,
        cellPadding: 2,
        overflow: "linebreak",
        halign: "left",
        valign: "middle",
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [31, 59, 179],
        textColor: 255,
        fontSize: 6,
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      margin: { top: 35, right: 10, bottom: 20, left: 10 },
    });

    doc.save("BuildMyTeam.pdf");
  };

  return (
    <Container fluid className="mt-4 px-4">
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <Row className="mb-4 align-items-center">
            <Col md={8}>
              <h3 className="fw-bold text-bold mb-0">Build My Team</h3>
            </Col>
            <Col md={4} className="text-end">
              <div className="d-flex justify-content-end align-items-center">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="me-2"
                >
                  Back
                </Button>
                <Dropdown className="d-inline-flex align-items-center">
                  <Dropdown.Toggle
                    className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
                    id="dropdown-export"
                    style={{ height: "34px" }}
                  >
                    <i className="icon-download me-1"></i> Export
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.Item onClick={exportToExcel}>
                      Export to Excel
                    </Dropdown.Item>
                    <Dropdown.Item onClick={exportToPDF}>
                      Export to PDF
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>
          </Row>

          <div className="bg-light p-3 rounded-3 border mb-4">
            {retreatDetail ? (
              <Row className="mb-1">
                <Col sm={12} md={12}>
                  <span className="fw-bold d-block mb-2">
                    {retreatDetail.title}
                  </span>
                  <span className="fw-bold">
                    {retreatDetail.dateFrom
                      ? moment(retreatDetail.dateFrom).format("MMM DD, YYYY")
                      : "N/A"}
                  </span>
                  <a
                    href={`/registration-retreat/${retreatDetail.id}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Retreat registration link"
                    style={{
                      color: "black",
                      display: "inline-flex",
                      alignItems: "center",
                      textDecoration: "none",
                    }}
                  >
                    {" "}
                    <span
                      className="ms-2 badge text-white"
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        backgroundColor: "#3190fa",
                        padding: "0.45em 0.8em",
                        borderRadius: "20px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Retreat ID: {retreatDetail.id}
                    </span>
                  </a>
                  <span
                    className="ms-2 badge text-white"
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      backgroundColor: "#3190fa",
                      padding: "0.45em 0.8em",
                      borderRadius: "20px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Parish ID:{" "}
                    {retreatDetail.myparish?.uniqueId ||
                      retreatDetail.parish?.uniqueId ||
                      retreatDetail.parishId ||
                      "N/A"}
                  </span>
                </Col>
              </Row>
            ) : (
              <div className="placeholder-glow">
                <span className="placeholder col-12 py-3 rounded-3"></span>
              </div>
            )}
          </div>

          <div
            className="filters-container p-4 rounded-4 mb-4"
            style={{
              background: "linear-gradient(to bottom, #ffffff, #fcfcfd)",
              border: "1px solid #eaebf0",
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            }}
          >
            <Row className="g-4">
              {/* Language Selection */}
              <Col md={3}>
                <label
                  className="form-label fw-bold d-block mb-3"
                  style={{ fontSize: "1rem" }}
                >
                  Language:
                </label>
                <div className="d-flex align-items-center gap-5 mt-2 p-3">
                  {languageOptions.map((lang) => (
                    <div
                      key={lang}
                      className="form-check form-check-inline m-0 d-flex align-items-center"
                    >
                      <input
                        type="checkbox"
                        className="form-check-input mt-0"
                        id={`check-${lang}`}
                        checked={selectedLanguages.includes(lang)}
                        onChange={() => handleLanguageChange(lang)}
                        style={{
                          cursor: "pointer",
                          width: "18px",
                          height: "18px",
                        }}
                      />
                      <label
                        className="form-check-label ms-2 mb-0 fw-normal"
                        htmlFor={`check-${lang}`}
                        style={{ cursor: "pointer", fontSize: "1rem" }}
                      >
                        {lang}
                      </label>
                    </div>
                  ))}
                </div>
              </Col>

              {/* Parish Multi-Select */}
              <Col md={5}>
                <label
                  className="form-label fw-bold mb-3 text-secondary"
                  style={{
                    fontSize: "0.9rem",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                  }}
                >
                  Parishes
                </label>
                <Select
                  isMulti
                  options={[
                    { value: "all", label: "Select All Parishes" },
                    { value: "none", label: "Deselect All Parishes" },
                    ...parishOptions,
                  ]}
                  value={selectedParishes}
                  onChange={handleParishChange}
                  placeholder="Select one or multiple parishes..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={{ Option: CustomOption }}
                  styles={{
                    ...customSelectStyles,
                    control: (base, state) => ({
                      ...customSelectStyles.control(base, state),
                      maxHeight: "150px",
                      overflowY: "auto",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      maxHeight: "140px",
                      overflowY: "auto",
                    }),
                  }}
                />
                <div className="mt-2 d-flex gap-3 px-1">
                  <span
                    className="small fw-semibold text-primary cursor-pointer hover-opacity"
                    style={{ cursor: "pointer", transition: "0.2s" }}
                    onClick={() => handleParishChange(parishOptions)}
                  >
                    Select All
                  </span>
                  <span
                    className="small fw-semibold text-danger cursor-pointer hover-opacity"
                    style={{ cursor: "pointer", transition: "0.2s" }}
                    onClick={() => handleParishChange([])}
                  >
                    Deselect All
                  </span>
                </div>
              </Col>

              {/* Filter Action */}
              <Col md={4}>
                <label
                  className="form-label fw-bold mb-3 text-secondary"
                  style={{
                    fontSize: "0.9rem",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                  }}
                >
                  Filter Options
                </label>
                <Select
                  options={filterOptions}
                  value={filterOptions.find(
                    (opt) => opt.value === filterOption,
                  )}
                  onChange={(opt) => setFilterOption(opt.value)}
                  placeholder="Select a filter..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={customSelectStyles}
                />
              </Col>
            </Row>
          </div>

          <Row className="mb-3">
            <Col className="d-flex justify-content-between align-items-center">
              <span className="badge bg-secondary px-3 py-2 fs-6 rounded-pill">
                Total Found: {data.length}
              </span>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="border rounded-4 shadow-sm overflow-hidden">
                <CustomDataTable
                  columns={columns}
                  data={data}
                  loading={loading}
                  pagination={true}
                  highlightOnHover={true}
                  pointerOnHover={false}
                  sortServer={true}
                  dataFetchApi={fetchData}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Container>
  );
};

export default BuildMyTeam;
